// Chart-spec JSON schema — the single source of truth for "is this
// chart spec object the right shape?" Used by:
//
//   • render/element/sbi/validate-render-op.ts (render-time / wire
//     validator) — embedded inside the `chart` op `oneOf` branch of
//     the larger RenderOp schema. Catches dynamic shapes that the
//     static save-time pass could not see (marker-resolved row
//     content, runtime spreads).
//
//   • authoring/validate-source.ts (save-time / static-AST validator,
//     track 2 of `docs/plans/chart-spec-save-time-validation.md`) —
//     compiled once via `compileChartSpecValidator()` and run against
//     statically-lowered `sbi.viz.<type>(spec)` literals.
//
// IMPORTANT — DO NOT inline or duplicate this schema in either of the
// callers above. Both paths must reference `CHART_SPEC_SCHEMA` directly
// so that a future change cannot land on one path without the other.
// If a third caller needs chart-spec shape, import the constant; do
// not copy-paste. The risk this defends against is silent save/render
// drift where a save passes against an old shape and a render fails
// against a new one.
//
// The schema mirrors the `ChartSpec` discriminated union exported from
// `protocol.ts` / `sbi.d.ts`; the per-type `required` rules in the
// `allOf` block below are the contract that union encodes at runtime.

import Ajv, { type ValidateFunction } from "ajv";

import { CHART_TYPES } from "./chart-types.ts";
import type { ChartSpec } from "../render/element/sbi/protocol";

/**
 * JSON Schema for the `spec` payload of a `chart` RenderOp.
 *
 * `additionalProperties: false` is strict — author-controlled props
 * can no longer flow through unchecked. Round-4 security M-4 caps
 * every author string; M-5 restricts colour fields to `var(--*)`
 * references — with the exception of the heatmap ramp endpoints
 * (`lowColor` / `highColor` / `midColor`) which also accept raw
 * CSS colour literals; see `HEATMAP_RAMP_COLOR_FIELD` below for the
 * rationale.
 *
 * Per-type required fields are pinned via the `allOf` block at the
 * bottom (each clause is an Ajv `if/then` on `spec.type`). The
 * cross-cutting `properties` listing keeps field caps in one place;
 * the `allOf` adds per-type structure plus the two conditional rules
 * (mixed.yAxes when any series uses yAxis ≥ 1; punchcard.{xLabel,yLabel}
 * when neither axis is on the temporal allowlist).
 *
 * Exported as a plain object so both Ajv instances (the wire path's
 * `allErrors: false` instance in validate-render-op.ts and the save
 * path's `allErrors: true` instance below) compile from the same
 * source.
 */

/**
 * Heatmap ramp endpoints (`lowColor` / `highColor` / `midColor`) —
 * shared schema fragment. Accepts either a `var(--…)` reference or a
 * raw CSS colour literal (hex 3-8 digit, rgb/rgba, hsl/hsla).
 *
 * Deliberately looser than the `var(--*)`-only pattern used by
 * `divergent.{positive,negative}Color` and `thresholds[].color`:
 * heatmap ramps are treated as a per-insight editorial choice, so
 * authors can pick a concrete hue without needing the embedder to
 * publish a matching token first. Canvas `fillStyle` silently
 * rejects unparseable strings, so an ill-formed literal is inert.
 */
const HEATMAP_RAMP_COLOR_FIELD = {
  type: "string",
  maxLength: 128,
  pattern:
    "^(var\\(--[\\w-]+\\)|#[0-9a-fA-F]{3,8}|rgba?\\([^)]{1,64}\\)|hsla?\\([^)]{1,64}\\))$",
} as const;

export const CHART_SPEC_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["type"],
  properties: {
    // Enum sourced from `chart-types.ts` so this validator can't
    // drift from the `ChartSpec` discriminated union or the host's
    // Vue dispatch.
    type: { enum: [...CHART_TYPES] },
    id: { type: "string", maxLength: 128 },
    primary: { type: "boolean" },
    title: { type: "string", maxLength: 256 },
    // Optional title-icon decoration. Closed enum is enforced
    // worker-side via validateOptIcon at render time; the schema only
    // caps the length here so a long unknown name can't slip past
    // save-time validation as payload bloat.
    titleIcon: { type: "string", maxLength: 64 },
    titleIconPosition: { enum: ["leading", "trailing"] },
    height: { type: "number", minimum: 0, maximum: 4096 },
    // DefaultOpts inherited via BaseChartSpec
    format: { type: "string", maxLength: 64 },
    description: { type: "string", maxLength: 1024 },
    empty: { type: "string", maxLength: 256 },
    error: { type: "string", maxLength: 1024 },
    // Data slot — array form gets the universal row cap; marker
    // form is an object the runtime resolves later. Per-type
    // tighter caps are applied host-side when the marker resolves.
    data: {
      oneOf: [
        {
          type: "array",
          maxItems: 10000,
          items: {
            type: "object",
            // Permit row-shape variation across chart types; row keys
            // are author-controlled column names.
            additionalProperties: true,
          },
        },
        // Marker descriptor — capped at 256 per round-4 security M-1.
        {
          type: "object",
          additionalProperties: false,
          properties: {
            __marker: { type: "string", maxLength: 256 },
          },
          required: ["__marker"],
        },
      ],
    },
    // Cross-cutting axis / column-name slots (column names are
    // capped at 64; row-shape lookup keys).
    x: { type: "string", maxLength: 64 },
    y: {
      oneOf: [
        { type: "string", maxLength: 64 },
        { type: "array", maxItems: 16, items: { type: "string", maxLength: 64 } },
      ],
    },
    xFormat: { type: "string", maxLength: 64 },
    yFormat: { type: "string", maxLength: 64 },
    valueFormat: { type: "string", maxLength: 64 },
    // Opt-in Y-axis domain control. Only the chart types with a numeric
    // y-axis (line, bar, stacked_bar, area, scatter) read this — other
    // types ignore it. `padding` is host-clamped to [0, 1]; the schema
    // pins the same range so invalid saves are rejected up-front.
    yScale: {
      type: "object",
      additionalProperties: false,
      required: ["mode"],
      properties: {
        mode: { enum: ["auto"] },
        padding: { type: "number", minimum: 0, maximum: 1 },
      },
    },
    value: {
      // Either a column name (most charts) or a marker (gauge), or
      // a literal scalar (gauge constants).
      oneOf: [
        { type: "string", maxLength: 64 },
        { type: "number" },
        {
          type: "object",
          additionalProperties: false,
          required: ["__marker"],
          properties: { __marker: { type: "string", maxLength: 256 } },
        },
        {
          type: "array",
          maxItems: 10000,
          items: { type: "object", additionalProperties: true },
        },
      ],
    },
    // Bar / scatter / heatmap / scatter. `color` is the column-name
    // slot for the third-dim colour encoding (scatter, etc.); not a
    // colour token. Renamed from `colour` in track 6 to align with
    // `annotations[].color` and `divergent.{positive,negative}Color`.
    horizontal: { type: "boolean" },
    color: { type: "string", maxLength: 64 },
    size: { type: "string", maxLength: 64 },
    colorScale: { enum: ["sequential", "diverging"] },
    // Heatmap ramp endpoints — shared shape hoisted above the schema
    // as `HEATMAP_RAMP_COLOR_FIELD`.
    lowColor: HEATMAP_RAMP_COLOR_FIELD,
    highColor: HEATMAP_RAMP_COLOR_FIELD,
    midColor: HEATMAP_RAMP_COLOR_FIELD,
    xOrder: {
      type: "array",
      maxItems: 256,
      items: { type: ["string", "number"] },
    },
    yOrder: {
      type: "array",
      maxItems: 256,
      items: { type: ["string", "number"] },
    },
    // Histogram
    field: { type: "string", maxLength: 64 },
    bins: { type: "integer", minimum: 1, maximum: 1024 },
    // Area
    stacked: { type: "boolean" },
    // Boxplot raw / pre-computed
    category: { type: "string", maxLength: 64 },
    quartiles: {
      type: "object",
      additionalProperties: false,
      required: ["min", "q1", "median", "q3", "max"],
      properties: {
        min: { type: "string", maxLength: 64 },
        q1: { type: "string", maxLength: 64 },
        median: { type: "string", maxLength: 64 },
        q3: { type: "string", maxLength: 64 },
        max: { type: "string", maxLength: 64 },
      },
    },
    outliers: { type: "string", maxLength: 64 },
    // Radar
    axes: {
      type: "array",
      maxItems: 32,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["name"],
        properties: {
          name: { type: "string", maxLength: 256 },
          min: { type: "number" },
          max: { type: "number" },
        },
      },
    },
    series: {
      type: "array",
      maxItems: 16,
      items: {
        type: "object",
        additionalProperties: false,
        properties: {
          // Radar series shape
          name: { type: "string", maxLength: 256 },
          values: {
            type: "array",
            maxItems: 32,
            items: { type: "number" },
          },
          // Mixed series shape — `kind`, not `type` (round-2 ask).
          kind: { enum: ["bar", "line"] },
          y: { type: "string", maxLength: 64 },
          yAxis: { type: "integer", minimum: 0, maximum: 3 },
        },
        required: ["name"],
      },
    },
    // Mixed dual-axis labelling — required when any series uses
    // yAxis > 0; enforced host-side.
    yAxes: {
      type: "array",
      maxItems: 4,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["label"],
        properties: {
          label: { type: "string", maxLength: 256 },
          format: { type: "string", maxLength: 64 },
        },
      },
    },
    // Waterfall
    totals: {
      type: "array",
      maxItems: 64,
      items: { type: "string", maxLength: 256 },
    },
    // Bar / waterfall divergent palette — colour fields take
    // `var(--*)` references only (round-4 security M-5).
    divergent: {
      type: "object",
      additionalProperties: false,
      properties: {
        positiveColor: { type: "string", maxLength: 128, pattern: "^var\\(--[\\w-]+\\)$" },
        negativeColor: { type: "string", maxLength: 128, pattern: "^var\\(--[\\w-]+\\)$" },
        zeroLine: { type: "boolean" },
      },
    },
    // Gauge
    min: { type: "number", minimum: -1e15, maximum: 1e15 },
    max: { type: "number", minimum: -1e15, maximum: 1e15 },
    thresholds: {
      type: "array",
      maxItems: 8,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["value"],
        properties: {
          value: { type: "number", minimum: -1e15, maximum: 1e15 },
          label: { type: "string", maxLength: 256 },
          // Renamed from `colour` in track 6; token rules unchanged.
          color: { type: "string", maxLength: 128, pattern: "^var\\(--[\\w-]+\\)$" },
        },
      },
    },
    // Donut
    inner: { type: "number", minimum: 0, maximum: 1 },
    // Punchcard non-temporal labels
    xLabel: { type: "string", maxLength: 256 },
    yLabel: { type: "string", maxLength: 256 },
    // Candlestick
    open: { type: "string", maxLength: 64 },
    high: { type: "string", maxLength: 64 },
    low: { type: "string", maxLength: 64 },
    close: { type: "string", maxLength: 64 },
    // Sunburst / tree — both consume flat `{ parent, name, value? }`
    // rows with an optional `idColumn` to scope identity by a
    // dedicated id column instead of by name.
    parent: { type: "string", maxLength: 64 },
    name: { type: "string", maxLength: 64 },
    idColumn: { type: "string", maxLength: 64 },
    // Tree-specific.
    orient: { enum: ["LR", "TB"] },
    initialDepth: { type: "number", minimum: 0, maximum: 64 },
    valueLabel: { enum: ["node", "tooltip", "off"] },
    colorBy: { type: "string", maxLength: 64 },
    // Sankey
    source: { type: "string", maxLength: 64 },
    target: { type: "string", maxLength: 64 },
    onCycle: { enum: ["warn", "self-loop", "drop"] },
    // Calendar
    date: { type: "string", maxLength: 64 },
    range: {
      oneOf: [
        {
          type: "array",
          minItems: 2,
          maxItems: 2,
          items: { type: "string", pattern: "^\\d{4}-\\d{2}-\\d{2}$" },
        },
        {
          type: "object",
          additionalProperties: false,
          required: ["end", "days"],
          properties: {
            end: { const: "today" },
            // Calendar-specific cap (1830 = 5y * 366) per round-4
            // security M-3, regardless of the broader DateRange
            // union's 36500 ceiling.
            days: { type: "integer", minimum: 1, maximum: 1830 },
          },
        },
        {
          type: "object",
          additionalProperties: false,
          required: ["window"],
          properties: {
            window: {
              enum: [
                "last_7d",
                "last_30d",
                "last_90d",
                "last_12m",
                "ytd",
                "this_month",
                "last_month",
                "this_quarter",
                "last_quarter",
                "this_year",
                "last_year",
              ],
            },
          },
        },
      ],
    },
    // DataZoom — same opt across line/bar/stacked_bar/area/heatmap/punchcard/calendar/mixed.
    dataZoom: {
      oneOf: [
        { enum: ["slider", "inside", "both"] },
        { type: "boolean" },
      ],
    },
    // Annotations on time-series charts. `color` restricted to
    // `var(--*)` references; `text` capped (round-4 security M-4).
    annotations: {
      type: "array",
      maxItems: 32,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["kind"],
        properties: {
          kind: { enum: ["hline", "vline", "band", "label"] },
          value: { type: ["string", "number"] },
          from: { type: ["string", "number"] },
          to: { type: ["string", "number"] },
          x: { type: ["string", "number"] },
          y: { type: "number" },
          text: { type: "string", maxLength: 256 },
          color: {
            type: "string",
            maxLength: 128,
            pattern: "^var\\(--[\\w-]+\\)$",
          },
        },
      },
    },
  },
  // Per-type required fields (round-2 plan #1). Each clause
  // pins additional `required` entries when `spec.type` matches
  // a discriminant. Conditional clauses (mixed.yAxes when
  // any series carries yAxis > 0; punchcard.{xLabel,yLabel}
  // when neither axis is temporal) live in dedicated `if/then`
  // entries below.
  allOf: [
    // ── Required-description charts (round-3 GM ask) ─────────
    { if: { properties: { type: { const: "gauge" } } },
      then: { required: ["type", "description", "value"] } },
    // ── Gauge value-shape tightening (track 4) ───────────────
    //
    // Gauge's `value` slot is single-scalar — it accepts a marker
    // (resolved to one number) or a literal number, never a
    // column-name string or row array. The base `value` `oneOf`
    // at chart-spec level allows all four (`string | number |
    // marker | array`) for the non-gauge case; this if/then
    // narrows gauge.
    //
    // The `number` branch is required because the worker
    // substitutes markers with a `0` placeholder before the
    // render-time validator runs. Banning numbers would break
    // every gauge render. The save-time path sees the marker
    // form (`{ __marker: "static" }` from the static-AST lowering)
    // and accepts it; a literal `value: "rate"` (column-name) is
    // the persona-review silent-failure case and gets rejected.
    { if: { properties: { type: { const: "gauge" } } },
      then: { properties: { value: { oneOf: [
        { type: "number" },
        { type: "object", required: ["__marker"], additionalProperties: false, properties: { __marker: { type: "string", maxLength: 256 } } },
      ] } } } },
    { if: { properties: { type: { const: "radar" } } },
      then: { required: ["type", "description", "axes", "series", "data"] } },
    { if: { properties: { type: { const: "sunburst" } } },
      then: { required: ["type", "description", "parent", "name", "value", "data"] } },
    { if: { properties: { type: { const: "tree" } } },
      then: { required: ["type", "description", "parent", "name", "data"] } },
    { if: { properties: { type: { const: "funnel" } } },
      then: { required: ["type", "description", "category", "value", "data"] } },
    // ── Other per-type required fields ───────────────────────
    { if: { properties: { type: { const: "line" } } },
      then: { required: ["type", "x", "y", "data"] } },
    { if: { properties: { type: { const: "bar" } } },
      then: { required: ["type", "x", "y", "data"] } },
    { if: { properties: { type: { const: "stacked_bar" } } },
      then: { required: ["type", "x", "y", "data"] } },
    { if: { properties: { type: { const: "scatter" } } },
      then: { required: ["type", "x", "y", "data"] } },
    { if: { properties: { type: { const: "histogram" } } },
      then: { required: ["type", "field", "data"] } },
    { if: { properties: { type: { const: "area" } } },
      then: { required: ["type", "x", "y", "data"] } },
    { if: { properties: { type: { const: "heatmap" } } },
      then: { required: ["type", "x", "y", "value", "data"] } },
    { if: { properties: { type: { const: "boxplot" } } },
      then: { required: ["type", "category", "data"] } },
    { if: { properties: { type: { const: "waterfall" } } },
      then: { required: ["type", "category", "value", "data"] } },
    { if: { properties: { type: { const: "mixed" } } },
      then: { required: ["type", "x", "series", "data"] } },
    { if: { properties: { type: { const: "polar_bar" } } },
      then: { required: ["type", "category", "value", "data"] } },
    { if: { properties: { type: { const: "donut" } } },
      then: { required: ["type", "category", "value", "data"] } },
    { if: { properties: { type: { const: "pie" } } },
      then: { required: ["type", "category", "value", "data"] } },
    { if: { properties: { type: { const: "punchcard" } } },
      then: { required: ["type", "x", "y", "size", "data"] } },
    { if: { properties: { type: { const: "candlestick" } } },
      then: { required: ["type", "x", "open", "high", "low", "close", "data"] } },
    { if: { properties: { type: { const: "sankey" } } },
      then: { required: ["type", "source", "target", "value", "data"] } },
    { if: { properties: { type: { const: "calendar" } } },
      then: { required: ["type", "date", "value", "range", "data"] } },
    // ── Conditional: mixed.yAxes required when any series uses yAxis > 0 ─
    // Ajv's `contains` matches when any array element satisfies
    // the inner schema. We require the spec to contain at least
    // one series with yAxis ≥ 1 to trigger the yAxes requirement.
    {
      if: {
        properties: {
          type: { const: "mixed" },
          series: {
            type: "array",
            contains: {
              type: "object",
              properties: {
                yAxis: { type: "integer", minimum: 1 },
              },
              required: ["yAxis"],
            },
          },
        },
        required: ["type", "series"],
      },
      then: { required: ["type", "series", "yAxes"] },
    },
    // ── Conditional: punchcard.{xLabel,yLabel} required when
    //    neither axis matches the temporal allowlist ──────────
    //
    // The temporal allowlist matches column names that
    // canonically read as time on a punchcard. When BOTH `x`
    // and `y` fall outside it, the chart is generic 2D-categorical
    // and the labels are required so viewers don't have to
    // infer axis meaning from the column-name strings.
    {
      if: {
        properties: {
          type: { const: "punchcard" },
          x: {
            type: "string",
            not: {
              pattern:
                "^(date|day|day_of_week|hour|hour_of_day|week|month|year|quarter|timestamp|created_at|updated_at)$",
            },
          },
          y: {
            type: "string",
            not: {
              pattern:
                "^(date|day|day_of_week|hour|hour_of_day|week|month|year|quarter|timestamp|created_at|updated_at)$",
            },
          },
        },
        required: ["type", "x", "y"],
      },
      then: { required: ["type", "x", "y", "size", "xLabel", "yLabel"] },
    },
  ],
} as const;

/**
 * Module-scope Ajv instance used by the save-time path.
 *
 * Distinct from validate-render-op.ts's instance: the save path runs
 * with `allErrors: true` so the suggester in `chart-spec-errors.ts`
 * (track 3) can rank multiple errors by severity. The render path
 * uses `allErrors: false` because at render time we only need the
 * first failure to surface a useful host-side error.
 *
 * Per the plan's Ajv-compilation-cost risk: compile once at module
 * load — never per-request. Both instances share the same schema
 * object by reference, so any future schema edit lands on both
 * paths automatically.
 */
const saveTimeAjv = new Ajv({ allErrors: true, allowUnionTypes: true });
const chartSpecValidator = saveTimeAjv.compile(CHART_SPEC_SCHEMA) as ValidateFunction<ChartSpec>;

/**
 * Returns the compiled save-time chart-spec validator. Pure accessor
 * over the module-scope cached `ValidateFunction` — calling this
 * repeatedly is free.
 */
export function compileChartSpecValidator(): ValidateFunction<ChartSpec> {
  return chartSpecValidator;
}
